import type { TimezoneMode } from "../types/models";

export function detectBrowserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
}

export function getEffectiveTimezone(mode: TimezoneMode, fixedTimezone: string): string {
  return mode === "fixed" && fixedTimezone ? fixedTimezone : detectBrowserTimezone();
}

export function getLocalDateInTimezone(date: Date, timezone: string): string {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  });
  return formatter.format(date);
}

export function toIsoNow(): string {
  return new Date().toISOString();
}

export function currentTimeInputValue(date = new Date()): string {
  return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
}

export function formatTimeInputInTimezone(value: string | undefined, timezone: string): string {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: timezone,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23"
  }).formatToParts(date);
  return `${readPart(parts, "hour")}:${readPart(parts, "minute")}`;
}

export function zonedDateTimeToIso(dateInput: string, timeInput: string, timezone: string): string {
  const [year, month, day] = dateInput.split("-").map(Number);
  const [hour, minute] = timeInput.split(":").map(Number);
  const utcGuess = new Date(Date.UTC(year, month - 1, day, hour, minute, 0, 0));
  const offset = getTimezoneOffsetMs(utcGuess, timezone);
  const firstPass = new Date(utcGuess.getTime() - offset);
  const correctedOffset = getTimezoneOffsetMs(firstPass, timezone);
  return new Date(utcGuess.getTime() - correctedOffset).toISOString();
}

function getTimezoneOffsetMs(date: Date, timezone: string): number {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23"
  }).formatToParts(date);
  const year = Number(readPart(parts, "year"));
  const month = Number(readPart(parts, "month"));
  const day = Number(readPart(parts, "day"));
  const hour = Number(readPart(parts, "hour"));
  const minute = Number(readPart(parts, "minute"));
  const second = Number(readPart(parts, "second"));
  return Date.UTC(year, month - 1, day, hour, minute, second) - date.getTime();
}

function readPart(parts: Intl.DateTimeFormatPart[], type: Intl.DateTimeFormatPartTypes): string {
  return parts.find((part) => part.type === type)?.value ?? "00";
}
